import { initData } from './tg';
import { useStore } from './store';
import type { AdminPaymentsResponse } from './types';

const API_BASE: string = import.meta.env.VITE_API_BASE ?? 'http://127.0.0.1:8080';
const REFRESH_KEY = 'gta5rp.refreshToken';

let jwt: string | null = null;

interface SessionResponse {
  jwt: string;
  refreshToken?: string;
}

interface ApiErrorBody {
  error?: { message?: string; code?: string };
}

function isSessionResponse(value: unknown): value is SessionResponse {
  return (
    typeof value === 'object' &&
    value !== null &&
    'jwt' in value &&
    typeof (value as { jwt: unknown }).jwt === 'string'
  );
}

function extractErrorMessage(value: unknown, fallback: string): string {
  if (typeof value === 'object' && value !== null) {
    const body = value as ApiErrorBody;
    const message = body.error?.message;
    if (typeof message === 'string' && message.length > 0) {
      return message;
    }
  }
  return fallback;
}

function getStoredRefresh(): string | null {
  try {
    return localStorage.getItem(REFRESH_KEY);
  } catch {
    return null;
  }
}

function setStoredRefresh(token: string): void {
  try {
    localStorage.setItem(REFRESH_KEY, token);
  } catch {
    /* storage unavailable — session is in-memory only */
  }
}

function clearStoredRefresh(): void {
  try {
    localStorage.removeItem(REFRESH_KEY);
  } catch {
    /* ignore */
  }
}

/** Telegram Mini App path: exchange initData for a session JWT. */
async function authenticateWithInitData(): Promise<void> {
  const res = await fetch(`${API_BASE}/v1/auth/session`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ initData }),
  });
  if (!res.ok) {
    const body: unknown = await res.json().catch(() => null);
    throw new Error(extractErrorMessage(body, `Auth failed: HTTP ${res.status}`));
  }
  const body: unknown = await res.json();
  if (!isSessionResponse(body)) throw new Error('Auth response missing jwt');
  jwt = body.jwt;
}

/** Web path: exchange a bot-issued 6-digit code for a session + refresh token. */
export async function loginWithCode(code: string): Promise<void> {
  const res = await fetch(`${API_BASE}/v1/auth/code`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ code }),
  });
  if (!res.ok) {
    const body: unknown = await res.json().catch(() => null);
    const fallback =
      res.status === 429
        ? 'Слишком много попыток. Попробуй позже.'
        : 'Неверный или просроченный код.';
    throw new Error(extractErrorMessage(body, fallback));
  }
  const body: unknown = await res.json();
  if (!isSessionResponse(body)) throw new Error('Auth response missing jwt');
  jwt = body.jwt;
  if (body.refreshToken) setStoredRefresh(body.refreshToken);
}

/** Web path: rotate the stored refresh token for a fresh JWT. */
async function refreshSession(): Promise<boolean> {
  const stored = getStoredRefresh();
  if (!stored) return false;
  const res = await fetch(`${API_BASE}/v1/auth/refresh`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ refreshToken: stored }),
  });
  if (!res.ok) {
    clearStoredRefresh();
    return false;
  }
  const body: unknown = await res.json();
  if (!isSessionResponse(body)) {
    clearStoredRefresh();
    return false;
  }
  jwt = body.jwt;
  if (body.refreshToken) setStoredRefresh(body.refreshToken);
  return true;
}

/** Revoke the refresh token server-side and clear local session state. */
export async function logout(): Promise<void> {
  const stored = getStoredRefresh();
  if (stored) {
    try {
      await fetch(`${API_BASE}/v1/auth/logout`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ refreshToken: stored }),
      });
    } catch {
      /* best-effort */
    }
  }
  clearStoredRefresh();
  jwt = null;
}

/**
 * Decide how to authenticate on boot:
 *  - inside Telegram (or dev initData) → initData session
 *  - else a stored refresh token → silent refresh
 *  - else the user must log in via a bot code
 */
export async function bootstrapAuth(): Promise<'authed' | 'needs-login'> {
  if (initData) {
    await authenticateWithInitData();
    return 'authed';
  }
  if (await refreshSession()) return 'authed';
  return 'needs-login';
}

/** Re-establish a session after a 401, picking the right strategy for the context. */
async function reauth(): Promise<boolean> {
  if (initData) {
    try {
      await authenticateWithInitData();
      return true;
    } catch {
      return false;
    }
  }
  return refreshSession();
}

/**
 * Unrecoverable auth failure. On web, surface the login screen; inside Telegram
 * just error out — initData re-authenticates on the next launch, so bouncing the
 * user to a code-entry screen there would be useless.
 */
function failAuth(): never {
  if (!initData) useStore.getState().setAuthStatus('needs-login');
  throw new Error('Not authenticated');
}

export async function api<T>(path: string, init?: RequestInit, retry = true): Promise<T> {
  if (!jwt) {
    const ok = await reauth();
    if (!ok) failAuth();
  }

  const headers: Record<string, string> = {
    'content-type': 'application/json',
    ...(init?.headers as Record<string, string> | undefined),
    Authorization: `Bearer ${jwt ?? ''}`,
  };

  const res = await fetch(`${API_BASE}${path}`, { ...init, headers });

  if (res.status === 401 && retry) {
    jwt = null;
    const ok = await reauth();
    if (!ok) failAuth();
    return api<T>(path, init, false);
  }

  if (!res.ok) {
    const body: unknown = await res.json().catch(() => null);
    throw new Error(extractErrorMessage(body, `HTTP ${res.status}`));
  }

  return (await res.json()) as T;
}

export function getApiBase(): string {
  return API_BASE;
}

export async function listAdminPayments(): Promise<AdminPaymentsResponse> {
  return api<AdminPaymentsResponse>('/v1/admin/payments');
}

export async function getAdminUserAvatar(tgId: number): Promise<{ url: string | null }> {
  return api<{ url: string | null }>(`/v1/admin/users/${tgId}/avatar`);
}
